import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';
import './requests.css';

export default function ToApproveTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed read must never read as "nothing awaiting your approval".
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // request id being rejected
  const [notes, setNotes] = useState('');

  const fetchRows = useCallback(() => {
    setLoading(true);
    setError('');
    axios.get(`${API_BASE_URL}/api/tool_requests`, { withCredentials: true })
      .then((res) => setRows((res.data || []).filter((r) => r.is_approver && r.status === 'pending_manager')))
      .catch((err) => setError(errorMessage(err, "Couldn't load proposals awaiting your approval. Try again.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const resolve = (id, decision, noteText) => {
    setBusyId(id);
    axios.patch(`${API_BASE_URL}/api/tool_requests/${id}/manager-resolve`,
      { decision, notes: noteText || undefined }, { withCredentials: true })
      .then(() => {
        toastSuccess(decision === 'approve' ? 'Proposal approved.' : 'Proposal declined.');
        setRejecting(null); setNotes('');
        fetchRows();
      })
      .catch((err) => toastError(errorMessage(err, 'Could not update the proposal')))
      .finally(() => setBusyId(null));
  };

  if (loading) {
    return <p className="rq-loading" role="status" aria-busy="true">Loading proposals…</p>;
  }
  if (error) {
    return (
      <div className="rq-error" role="alert">
        <span>{error}</span>
        <button type="button" className="btn btn-secondary btn-small" onClick={fetchRows}>Try again</button>
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="rq-empty"><p>Nothing awaiting your approval.</p></div>;
  }

  return (
    <ul className="rq-approve-list">
      {rows.map((r) => {
        const safeLink = /^https?:\/\//i.test(r.link || '') ? r.link : null;
        const busy = busyId === r.id;
        const reasonId = `rq-decline-reason-${r.id}`;
        return (
          <li key={r.id} className="rq-approve-item">
            <div className="rq-approve-name">{r.name}</div>
            {r.description && <div className="rq-approve-desc">{r.description}</div>}
            <div className="rq-approve-meta">
              Proposed by {r.requester_name} · {safeLink
                ? <a className="rq-link" href={safeLink} target="_blank" rel="noopener noreferrer">{safeLink}</a>
                : <span>{r.link}</span>}
            </div>
            {rejecting === r.id ? (
              <div className="form-group">
                <label htmlFor={reasonId}>Reason for declining (optional)</label>
                <textarea id={reasonId} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                <div className="rq-actions">
                  <button type="button" className="btn btn-danger" disabled={busy} onClick={() => resolve(r.id, 'reject', notes)}>
                    {busy ? 'Declining…' : 'Confirm decline'}
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setRejecting(null); setNotes(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="rq-actions">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => resolve(r.id, 'approve')}>
                  {busy ? 'Approving…' : 'Approve'}
                </button>
                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setRejecting(r.id)}>
                  Decline
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
